import plans from "_shared/constants/plans";
import { useModal } from "_shared/contexts/ModalContext";
import { API } from "aws-amplify";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  UserSubscriptionsQuery,
  useUserSubscriptionsQuery,
} from "../../../_shared/graphql";

type PlanChange = "upgrade" | "downgrade" | "cancel" | null;

const SubscriptionsContext = createContext({
  billingDate: new Date() as Date | undefined,
  planChange: null as PlanChange | null,
  currentPlanIndex: 0,
  selectedPlanIndex: 0,
  setSelectedPlanIndex: (_index: number) => {},
  expiringPlanIndex: undefined as number | undefined,
  selectedAddons: [] as string[],
  expiringAddons: [] as string[],
  toggleAddon: (_subId: string) => {},
  confirmSubscriptionChange: async (
    _addedPriceIds: string[],
    _removedPriceIds: string[],
  ) => {},
  onCancel: () => {},
  userSubscriptions: [] as UserSubscriptionsQuery["subscriptions"],
  cancelDowngrade: () => {},
  reactivatePlan: () => {},
});

export const SubscriptionsProvider: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const { awaitConfirmation, onSuccessMsg, onErrorMsg } = useModal();

  const { data: userSubscriptionData, refetch: refetchUserSubs } =
    useUserSubscriptionsQuery();

  const [currentPlanIndex, setCurrentPlanIndex] = useState(-1);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [expiringPlanIndex, setExpiringPlanIndex] = useState<number>();
  const [planChange, setPlanChange] = useState<PlanChange>(null);

  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [expiringAddons, setExpiringAddons] = useState<string[]>([]);

  const userSubscriptions = useMemo(() => {
    if (!userSubscriptionData) return [];
    return userSubscriptionData.subscriptions.filter((sub) => sub.subscribed);
  }, [userSubscriptionData]);

  const [billingDate, setBillingDate] = useState<Date>();

  // set initial selected subscription ids
  useEffect(() => {
    // initialise plan
    const currentPlan = userSubscriptions.find(
      (sub) => sub.id.endsWith("plan") && !sub.subscribed!.expires,
    );
    if (currentPlan) {
      const planIndex = plans.findIndex((plan) => plan.key === currentPlan.id);
      // set current plan and initialise selected plan
      setCurrentPlanIndex(planIndex);
      setSelectedPlanIndex(planIndex);
    } else {
      setCurrentPlanIndex(-1);
    }
    const expiringPlan = userSubscriptions.find(
      (sub) => sub.id.endsWith("plan") && sub.subscribed!.expires,
    );
    if (expiringPlan) {
      setExpiringPlanIndex(
        plans.findIndex((plan) => plan.key === expiringPlan?.id),
      );
    }
    // initialise addons
    const currentAddons = userSubscriptions.filter(
      (sub) => !sub.id.endsWith("plan"),
    );
    if (currentAddons) {
      setSelectedAddons(
        currentAddons
          .filter((sub) => !sub.subscribed!.expires)
          .map((sub) => sub.id),
      );
      setExpiringAddons(
        currentAddons
          .filter((sub) => sub.subscribed!.expires)
          .map((sub) => sub.id),
      );
    }
  }, [userSubscriptions]);

  // get and set billing date
  useEffect(() => {
    const getBillingDate = async () => {
      const res = await API.post("auth", "/cognito/getBillingDate", {});
      const newBillingDate = new Date(res.billingDate);
      setBillingDate(newBillingDate);
    };
    getBillingDate();
  }, []);

  useEffect(() => {
    if (selectedPlanIndex === null) {
      setPlanChange("cancel");
    } else if (selectedPlanIndex > currentPlanIndex) {
      setPlanChange("upgrade");
    } else if (selectedPlanIndex === currentPlanIndex) {
      setPlanChange(null);
    } else {
      setPlanChange("downgrade");
    }
  }, [currentPlanIndex, selectedPlanIndex]);

  const toggleAddon = (toggleSubId: string) => {
    // toggle off subscription addon
    if (selectedAddons.includes(toggleSubId))
      return setSelectedAddons((prev) =>
        prev.filter((sub) => sub !== toggleSubId),
      );
    // toggle on subscription addon
    return setSelectedAddons((prev) => [...prev, toggleSubId]);
  };

  const confirmSubscriptionChange = async (
    addedPriceIds: string[],
    removedPriceIds: string[],
  ) => {
    const res = await API.post("auth", "/cognito/changeSubscription", {
      body: { addedPriceIds, removedPriceIds },
    });
    if (res?.code === "Success") {
      await refetchUserSubs();
      onSuccessMsg("Your subscription has been updated.");
    } else {
      onErrorMsg("There was a problem updating your subscription.");
      console.error(res);
      throw new Error(res);
    }
  };

  const onCancel = () => {
    awaitConfirmation(
      async () => {
        const res = await API.post("auth", "/cognito/cancelSubscription", {});
        if (res?.code === "Success") {
          await refetchUserSubs();
          onSuccessMsg("Your subscription has been cancelled.");
        } else {
          onErrorMsg("There was a problem cancelling your subscription.");
          console.error(res);
          throw new Error(res);
        }
      },
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <span>
          {`Your subscription along with all your add-ons and tickers will be removed. If you would like
        to continue, click confirm below. If you would like to change to a different
        subscription, click cancel to go back.`}
        </span>
        <span>Are you sure you would like to unsubscribe?</span>
      </div>,
    );
  };

  const reactivatePlan = async () => {
    setCurrentPlanIndex(expiringPlanIndex || 0);
  };

  return (
    <SubscriptionsContext.Provider
      value={{
        billingDate,
        planChange,
        currentPlanIndex,
        selectedPlanIndex,
        setSelectedPlanIndex,
        expiringPlanIndex,
        selectedAddons,
        expiringAddons,
        toggleAddon,
        confirmSubscriptionChange,
        onCancel,
        userSubscriptions,
        cancelDowngrade: () => setSelectedPlanIndex(currentPlanIndex),
        reactivatePlan,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
};

export const useSubscriptionsContext = () => useContext(SubscriptionsContext);
